/**
 * window.FlexoBridge — SignalR interop for the Unity WebGL build.
 *
 * The Unity side (FlexoSignalR.cs + FlexoGame.jslib) does NOT open its own
 * SignalR connection in WebGL. Instead the jslib delegates to
 * `window.FlexoBridge.{connectSignalR,disconnectSignalR,sendSignalRMessage}`
 * and expects callbacks via SendMessage to the `FlexoSignalR` GameObject:
 *   - OnSignalRConnected(connectionId)
 *   - OnSignalRDisconnected(error)
 *   - OnSignalRMessage('{"method":"...","args":"<json string>"}')
 *
 * This module is also a TRANSLATION LAYER between the method/event names the
 * FlexoRoomController uses and the contract the production RoomHub actually
 * exposes:
 *
 *   Unity invoke                    → RoomHub method
 *   JoinRoom(code,id,name,app,cid)  → JoinRoom(roomId, playerInfo)
 *   GetRoomRoster(roomId)           → GetRoomRoster(roomId), relayed back as the
 *                                     RoomRosterResult event (latecomer visibility)
 *   PlayerSpawned(roomId, json)     → NotifyPlayerSpawned(roomId, json)
 *   UpdatePlayerPosition(roomId, j) → SyncPlayer(playerDataJson)  [string passthrough]
 *   UpdatePlayerAppearance(room,id,json) → UpdatePlayerAppearance(roomId, appearanceJson)
 *                                     (older servers lack the method; warn once)
 *
 *   RoomHub event                   → Unity handler name
 *   PlayerJoinedRoom(roomId, json)  → PlayerJoinedRoom {roomId, payload}
 *   PlayerReconnected(roomId, json) → PlayerJoinedRoom {roomId, payload}
 *   PlayerSpawned(roomId, json)     → PlayerSpawned {roomId, payload}
 *   PlayerLeftRoom(roomId, userId)  → PlayerLeftRoom (userId only)
 *   ReceivePlayerSync(json)         → PlayerPositionUpdate (unwrapped .flexo)
 *   PlayerAppearanceUpdated(roomId, userId, json) → PlayerAppearanceUpdated {roomId, userId, payload}
 *   SceneConfigurationUpdated(json) → SceneConfigurationUpdated (passthrough)
 *
 * Position updates are wrapped in a SignalRPlayerData-shaped envelope with the
 * original Unity payload under `flexo`, because the hub's UpdateMovement
 * endpoint deserializes into System.Numerics types that System.Text.Json
 * cannot round-trip (public fields, IncludeFields not enabled) — SyncPlayer
 * re-broadcasts the raw string losslessly instead.
 */
import * as signalR from '@microsoft/signalr';
import { devLog } from '../../utils/logger';

type UnitySendMessage = (gameObjectName: string, methodName: string, parameter?: string | number) => void;

const SIGNALR_GO = 'FlexoSignalR';
const TOKEN_KEY = 'gamedate_token';

interface FlexoPositionUpdate {
  playerId: string;
  position?: { x: number; y: number; z: number };
  rotation?: number;
  velocityX?: number;
  velocityY?: number;
  velocityZ?: number;
  animState?: string;
  zone?: string;      // multi-room-world Phase 2: current zone id
  configId?: string;  // multi-room-world Phase 2: SceneConfiguration the client built
}

class FlexoBridgeSignalR {
  private connection: signalR.HubConnection | null = null;
  private unitySendMessage: UnitySendMessage | null = null;
  private connecting = false;
  private currentRoomId: string | null = null;
  private localPlayerId: string | null = null;
  private warnedMethods = new Set<string>();
  // Room whose spawn-restore payload the server already pushed during JoinRoom
  // (mobile-loading P2 B3) — lets us skip the redundant GetMySpawn invoke.
  private spawnPushedRoomId: string | null = null;

  attachUnity(sendMessage: UnitySendMessage) {
    this.unitySendMessage = sendMessage;
    // Allow the "not mounted" notice to log once more next time Unity unmounts.
    this.warnedMethods.delete('unity-send-unavailable');
  }

  detachUnity() {
    this.unitySendMessage = null;
  }

  // ---- jslib entry points (called from Unity WASM) ----

  connectSignalR = (url: string, token: string): void => {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      this.notifyConnected(this.connection.connectionId ?? '');
      return;
    }
    if (this.connecting) return;
    this.connecting = true;

    const builder = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => token || localStorage.getItem(TOKEN_KEY) || '',
      })
      .withAutomaticReconnect([0, 1000, 3000, 5000, 10000])
      .configureLogging(signalR.LogLevel.Warning);

    const connection = builder.build();
    this.connection = connection;
    this.registerServerEvents(connection);

    connection.onreconnecting((err) => {
      console.warn('[FlexoBridge] SignalR reconnecting:', err?.message);
      this.sendToUnity('OnSignalRDisconnected', err?.message ?? 'reconnecting');
    });
    connection.onreconnected((connectionId) => {
      devLog('[FlexoBridge] SignalR reconnected:', connectionId);
      this.notifyConnected(connectionId ?? '');
    });
    connection.onclose((err) => {
      console.warn('[FlexoBridge] SignalR closed:', err?.message);
      this.connecting = false;
      this.sendToUnity('OnSignalRDisconnected', err?.message ?? 'closed');
    });

    connection
      .start()
      .then(() => {
        this.connecting = false;
        devLog('[FlexoBridge] SignalR connected:', connection.connectionId);
        this.notifyConnected(connection.connectionId ?? '');
      })
      .catch((err) => {
        this.connecting = false;
        console.error('[FlexoBridge] SignalR connect failed:', err);
        this.sendToUnity('OnSignalRDisconnected', String(err?.message ?? err));
      });
  };

  disconnectSignalR = (): void => {
    const conn = this.connection;
    this.connection = null;
    this.connecting = false;
    this.currentRoomId = null;
    conn?.stop().catch(() => undefined);
  };

  sendSignalRMessage = (method: string, argsJson: string): void => {
    let args: unknown[] = [];
    try {
      args = argsJson ? (JSON.parse(argsJson) as unknown[]) : [];
    } catch (e) {
      console.error('[FlexoBridge] Bad args JSON for', method, e);
      return;
    }
    this.translateAndInvoke(method, args).catch((err) => {
      console.warn(`[FlexoBridge] Invoke ${method} failed:`, err?.message ?? err);
    });
  };

  // Token helpers used by FlexoGame.jslib (JS_Get/Set/ClearAuthToken).
  getAuthToken = (): string => localStorage.getItem(TOKEN_KEY) ?? '';
  setAuthToken = (_token: string): void => undefined;
  clearAuthToken = (): void => undefined;

  // ---- Unity -> hub translation ----

  private async translateAndInvoke(method: string, args: unknown[]): Promise<void> {
    const conn = this.connection;
    if (!conn || conn.state !== signalR.HubConnectionState.Connected) {
      console.warn('[FlexoBridge] Not connected, dropping invoke:', method);
      return;
    }

    switch (method) {
      case 'JoinRoom': {
        const [roomCode, playerId, displayName, appearanceData] = args as string[];
        this.currentRoomId = roomCode;
        this.localPlayerId = playerId;
        this.spawnPushedRoomId = null;
        await conn.invoke('JoinRoom', roomCode, {
          id: playerId,
          displayName,
          appearanceData: appearanceData ?? '',
        });
        return;
      }
      case 'LeaveRoom': {
        const [roomId] = args as string[];
        this.currentRoomId = null;
        this.spawnPushedRoomId = null;
        await conn.invoke('LeaveRoom', roomId);
        return;
      }
      case 'PlayerSpawned': {
        const [roomId, spawnJson] = args as string[];
        await conn.invoke('NotifyPlayerSpawned', roomId, spawnJson);
        return;
      }
      case 'UpdatePlayerPosition': {
        const [roomId, updateJson] = args as string[];
        let update: FlexoPositionUpdate;
        try {
          update = JSON.parse(updateJson) as FlexoPositionUpdate;
        } catch {
          return;
        }
        const envelope = {
          playerId: update.playerId,
          playerName: '',
          lobbyId: roomId,
          characterType: '',
          avatarGuid: '',
          position: update.position ?? { x: 0, y: 0, z: 0 },
          // Top-level copies for the server's SignalRPlayerData model (the hub never
          // parses the nested flexo payload) — powers durable position persistence.
          rotY: update.rotation ?? 0,
          zone: update.zone ?? '',
          configId: update.configId ?? '',
          flexo: update,
        };
        await conn.invoke('SyncPlayer', JSON.stringify(envelope));
        return;
      }
      case 'UpdatePlayerAppearance': {
        // Actual Unity contract (FlexoRoomController.UpdateLocalPlayerAppearance):
        //   InvokeAsync("UpdatePlayerAppearance", playerId, appearanceJson)
        // — 2 args, playerId first, NO room code. Also tolerate a 3-arg
        // (roomCode, playerId, appearanceJson) shape from future builds, same
        // flexible arg handling as JoinRoom. The hub method takes
        // (roomId, appearanceJson); the server attributes the change to the
        // authenticated caller, so playerId is dropped.
        const strArgs = args as string[];
        let roomId: string;
        let appearanceJson: string;
        if (strArgs.length >= 3) {
          roomId = strArgs[0] || this.currentRoomId || '';
          appearanceJson = strArgs[2] ?? '';
        } else {
          roomId = this.currentRoomId ?? '';
          appearanceJson = strArgs[1] ?? '';
        }
        if (!roomId || !appearanceJson) return;
        try {
          await conn.invoke('UpdatePlayerAppearance', roomId, appearanceJson);
        } catch (err) {
          // Older servers don't expose this hub method — degrade silently after one warn.
          this.warnOnce(
            method,
            `[FlexoBridge] UpdatePlayerAppearance not supported by server: ${(err as Error)?.message ?? err}`,
          );
        }
        return;
      }
      case 'GetRoomRoster': {
        // Latecomer visibility (Alchemist's Cove P0): relay the authoritative roster back
        // to Unity as a RoomRosterResult event — WebGL cannot receive invoke return values,
        // and the old GetPlayersInRoom translation dropped the result entirely, so late
        // joiners never saw players already in the room. Same relay shape as GetMySpawn.
        // Always answer (empty roster on error) so Unity's await resolves without timeout.
        const [roomId] = args as string[];
        let rosterJson = '{"roomId":"","players":[]}';
        try {
          const result = await conn.invoke<string>('GetRoomRoster', roomId);
          if (result) rosterJson = result;
        } catch (err) {
          this.warnOnce(
            method,
            `[FlexoBridge] GetRoomRoster not supported by server: ${(err as Error)?.message ?? err}`,
          );
        }
        this.forward('RoomRosterResult', rosterJson);
        return;
      }
      case 'GetMySpawn': {
        // Durable spawn restore (multi-room-world Phase 2). Same relay shape as
        // GetSceneConfiguration: WebGL Unity cannot receive invoke return values, so
        // the result comes back as a MySpawnResult event. Old servers without the hub
        // method degrade to "no restore" so the client's slot-spawn fallback runs.
        const [roomId] = args as string[];
        if (roomId && roomId === this.spawnPushedRoomId) {
          // JoinRoom already pushed this room's MySpawnResult (P2 B3) — invoking
          // again would just queue behind other in-flight hub invocations.
          devLog('[FlexoBridge] GetMySpawn skipped — JoinRoom already pushed the result');
          return;
        }
        let spawnJson = '{"hasRestore":false}';
        try {
          const result = await conn.invoke<string>('GetMySpawn', roomId);
          if (result) spawnJson = result;
        } catch (err) {
          this.warnOnce(
            method,
            `[FlexoBridge] GetMySpawn not supported by server: ${(err as Error)?.message ?? err}`,
          );
        }
        this.forward('MySpawnResult', spawnJson);
        return;
      }
      case 'GetSceneConfiguration': {
        // WebGL Unity cannot receive invoke return values (FlexoSignalR's
        // InvokeAsync<T> returns default), so relay the result as the
        // SceneConfigurationUpdated event Unity already handles — without
        // this the client always fell back to an EMPTY default scene.
        const [roomId, configId] = args as string[];
        const configJson = await conn.invoke<string>('GetSceneConfiguration', roomId, configId ?? '');
        if (configJson) {
          this.forward('SceneConfigurationUpdated', configJson);
        }
        return;
      }
      default:
        await conn.invoke(method, ...args);
    }
  }

  // ---- hub -> Unity translation ----

  private registerServerEvents(conn: signalR.HubConnection) {
    conn.on('PlayerJoinedRoom', (roomId: string, playerJson: string) => {
      this.forward('PlayerJoinedRoom', JSON.stringify({ roomId, payload: playerJson }));
    });
    conn.on('PlayerReconnected', (roomId: string, playerJson: string) => {
      this.forward('PlayerJoinedRoom', JSON.stringify({ roomId, payload: playerJson }));
    });
    conn.on('PlayerSpawned', (roomId: string, spawnJson: string) => {
      this.forward('PlayerSpawned', JSON.stringify({ roomId, payload: spawnJson }));
    });
    conn.on('PlayerLeftRoom', (_roomId: string, userId: string) => {
      this.forward('PlayerLeftRoom', userId);
    });
    conn.on('SceneConfigurationUpdated', (configJson: string) => {
      this.forward('SceneConfigurationUpdated', configJson);
    });
    conn.on('MySpawnResult', (roomId: string, spawnJson: string) => {
      // Pushed by the hub during JoinRoom itself (mobile-loading P2 B3), so the
      // client no longer waits for a separate GetMySpawn invoke to clear the
      // hub's sequential invocation queue. Unity's handler and payload shape are
      // identical to the invoke-relay path above.
      if (roomId && this.currentRoomId && roomId !== this.currentRoomId) return;
      this.spawnPushedRoomId = roomId || this.currentRoomId;
      this.forward('MySpawnResult', spawnJson);
    });
    conn.on('SocialEventReceived', (envelopeJson: string) => {
      // Alchemist's Cove P2: server-validated social events (potion throws, cheers,
      // shake-offs; P4 adds puzzle deltas). Unity replays the visual from the
      // authoritative broadcast — passthrough, no filtering (throws are room-public).
      this.forward('SocialEventReceived', envelopeJson);
    });
    conn.on('PlayerAppearanceUpdated', (roomId: string, userId: string, appearanceJson: string) => {
      // Same filters as ReceivePlayerSync: skip our own echo and other rooms.
      if (userId && userId === this.localPlayerId) return;
      if (roomId && this.currentRoomId && roomId !== this.currentRoomId) return;
      this.forward('PlayerAppearanceUpdated', JSON.stringify({ roomId, userId, payload: appearanceJson }));
    });
    // Dungeon Master relay (P4): admin façade → live room. Spawned payload is a
    // single JSON string; StateChanged's two args are packed for Unity's parser.
    conn.on('DungeonMasterObjectSpawned', (objectDataJson: string) => {
      this.forward('DungeonMasterObjectSpawned', objectDataJson);
    });
    conn.on('DungeonMasterObjectDestroyed', (objectId: string) => {
      this.forward('DungeonMasterObjectDestroyed', objectId);
    });
    conn.on('DungeonMasterObjectStateChanged', (objectId: string, stateJson: string) => {
      this.forward('DungeonMasterObjectStateChanged', JSON.stringify({ objectId, stateJson }));
    });
    conn.on('DungeonMaster', (messageJson: string) => {
      this.forward('DungeonMaster', messageJson);
    });
    conn.on('ReceivePlayerSync', (playerDataJson: string) => {
      try {
        const data = JSON.parse(playerDataJson) as { playerId?: string; lobbyId?: string; flexo?: FlexoPositionUpdate };
        if (!data.flexo) return;
        if (data.playerId && data.playerId === this.localPlayerId) return;
        if (data.lobbyId && this.currentRoomId && data.lobbyId !== this.currentRoomId) return;
        this.forward('PlayerPositionUpdate', JSON.stringify(data.flexo));
      } catch {
        // Not a flexo envelope (e.g. legacy GameDate-UI client) — ignore.
      }
    });
  }

  private forward(method: string, args: string) {
    this.sendToUnity('OnSignalRMessage', JSON.stringify({ method, args }));
  }

  private notifyConnected(connectionId: string) {
    this.sendToUnity('OnSignalRConnected', connectionId);
  }

  private sendToUnity(handler: string, parameter: string) {
    const send = this.unitySendMessage ?? (window as Window & { sendUnityMessage?: UnitySendMessage }).sendUnityMessage;
    if (!send) {
      // Unity isn't mounted (e.g. the user switched away from the game view) — the
      // SignalR connection stays open and keeps streaming player-sync frames. Dropping
      // them is correct (there's nothing to render); warn ONCE instead of ~20x/sec.
      this.warnOnce('unity-send-unavailable',
        '[FlexoBridge] Unity not mounted — skipping SignalR→Unity forwarding until it remounts');
      return;
    }
    try {
      send(SIGNALR_GO, handler, parameter);
    } catch (e) {
      console.warn('[FlexoBridge] SendMessage failed:', handler, e);
    }
  }

  private warnOnce(key: string, message: string) {
    if (this.warnedMethods.has(key)) return;
    this.warnedMethods.add(key);
    console.warn(message);
  }
}

export const flexoBridge = new FlexoBridgeSignalR();

/** Install window.FlexoBridge before the Unity WASM boots. Idempotent. */
export function installFlexoBridge(): void {
  const w = window as Window & { FlexoBridge?: unknown };
  if (w.FlexoBridge === flexoBridge) return;
  w.FlexoBridge = flexoBridge;
  devLog('[FlexoBridge] window.FlexoBridge installed');
}

installFlexoBridge();
